import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Package, User, BookOpen, Award, Layers, MapPin,
  Settings, LogOut, Menu, X, Users, Sparkles, UserCircle,
} from "lucide-react";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import SEO from "@/components/SEO";
import { VerifyEmailSidebarNotice } from "@/pages/collector/VerifyEmailNotice";

// Sprint · Profile-first Dashboard.
// `/dashboard` now renders MyProfile (the collector's digital home).
// "My Collection" is a distinct album-management surface. The old
// Phase C2a — the "Discover Albums" sidebar entry has been removed
// because it pointed to the same route as My Collection (only passing
// `state.openAdd: true`), creating a duplicate active-navigation
// state. The Add Album button inside My Collection remains the
// canonical entry point for browsing / adding new albums. Legacy
// deep-links to /dashboard/my-collection?openAdd=1 still work — the
// state trigger inside MyCollection is untouched.
const NAV_ITEMS = [
  { key: "my-profile",    to: "/dashboard",                    label: "My Profile",         Icon: UserCircle, enabled: true, end: true },
  { key: "my-collection", to: "/dashboard/my-collection",      label: "My Collection",      Icon: BookOpen,   enabled: true },
  { key: "certificates",  to: "/dashboard/certificates",       label: "Certificates",       Icon: Award,      enabled: true },
  { key: "orders",        to: "/dashboard/orders",             label: "Orders",             Icon: Package,    enabled: true },
  { key: "smart-matches", to: "/dashboard/smart-matches",      label: "Smart Matches",      Icon: Sparkles,   enabled: true },
  { key: "matching",      to: "/dashboard/matching",           label: "Privacy & Matching", Icon: Users,      enabled: true },
  { key: "tracker",       to: "/dashboard/collection-tracker", label: "Collection Tracker", Icon: Layers,     enabled: false },
  { key: "addresses",     to: "/dashboard/addresses",          label: "Saved Addresses",    Icon: MapPin,     enabled: false },
  { key: "settings",      to: "/dashboard/account-settings",   label: "Account Settings",   Icon: Settings,   enabled: true },
  { key: "edit-profile",  to: "/dashboard/collector-profile",  label: "Edit Profile",       Icon: User,       enabled: true, indent: true },
];

export default function DashboardLayout() {
  const { collector, logout } = useCollectorAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (!collector) return null;

  const signOut = async () => {
    await logout();
    toast.success("Signed out.");
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-[calc(100vh-140px)] bg-[var(--cream)]" data-testid="dashboard-layout">
      <SEO title="My Account · CollectorUp" />

      {/* Mobile top bar */}
      <div className="lg:hidden border-b-2 border-[var(--ink)] bg-white sticky top-0 z-30" data-testid="dashboard-mobile-topbar">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            data-testid="dashboard-menu-open"
            className="inline-flex items-center gap-2 border-2 border-[var(--ink)] rounded-lg px-3 py-1.5 text-sm font-bold"
            aria-label="Open dashboard navigation"
          >
            <Menu size={18} /> Menu
          </button>
          <div className="font-display text-lg tracking-tight text-[var(--ink)]">My Account</div>
          <div className="w-9 h-9 rounded-full bg-[var(--panini-yellow-soft)] border-2 border-[var(--ink)] grid place-items-center font-bold text-sm">
            {(collector.first_name || collector.display_name || "?").charAt(0).toUpperCase()}
          </div>
        </div>
      </div>

      <div className="container-gm py-6 lg:py-10">
        <div className="grid lg:grid-cols-[260px_1fr] gap-6 lg:gap-10">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block" data-testid="dashboard-sidebar">
            <SidebarInner collector={collector} onSignOut={signOut} />
          </aside>

          {/* Mobile drawer */}
          {drawerOpen && (
            <div className="lg:hidden fixed inset-0 z-50" data-testid="dashboard-mobile-drawer" role="dialog" aria-modal="true">
              <div
                className="absolute inset-0 bg-black/60"
                onClick={() => setDrawerOpen(false)}
              />
              <aside className="absolute inset-y-0 left-0 w-[280px] max-w-[85vw] bg-white border-r-2 border-[var(--ink)] overflow-y-auto shadow-2xl">
                <div className="flex items-center justify-between p-4 border-b-2 border-[var(--ink)]">
                  <div className="font-display text-lg">Menu</div>
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(false)}
                    data-testid="dashboard-menu-close"
                    className="p-1.5 rounded-md hover:bg-[var(--cream)]"
                    aria-label="Close menu"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="p-4">
                  <SidebarInner
                    collector={collector}
                    onSignOut={signOut}
                    onNavigate={() => setDrawerOpen(false)}
                  />
                </div>
              </aside>
            </div>
          )}

          {/* Main content */}
          <main data-testid="dashboard-content" className="min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}

function SidebarInner({ collector, onSignOut, onNavigate }) {
  const verified = collector.account_status === "active" && collector.email_verified_at;
  return (
    <div className="space-y-5">
      {/* Identity card */}
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 shadow-[4px_4px_0_var(--ink)]" data-testid="sidebar-identity">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[var(--panini-yellow-soft)] border-2 border-[var(--ink)] grid place-items-center font-bold text-lg shrink-0">
            {(collector.first_name || collector.display_name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-display text-base truncate" data-testid="sidebar-name">
              {collector.display_name || `${collector.first_name || ""} ${collector.last_name || ""}`.trim() || "Collector"}
            </div>
            <div className="text-[11px] text-[var(--smoke)] font-mono truncate" data-testid="sidebar-public-id">
              {collector.public_collector_id || "Public ID pending"}
            </div>
          </div>
        </div>
        {!verified && (
          <VerifyEmailSidebarNotice />
        )}
      </div>

      {/* Navigation */}
      <nav className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-hidden" data-testid="sidebar-nav">
        {NAV_ITEMS.map(({ key, to, label, Icon, enabled, end, exploreCta, indent }) => (
          <NavItem
            key={key}
            to={to}
            state={exploreCta ? { openAdd: true } : undefined}
            Icon={Icon}
            label={label}
            enabled={enabled}
            end={end}
            indent={indent}
            onNavigate={onNavigate}
            testid={`sidebar-nav-${key}`}
          />
        ))}
        <button
          type="button"
          onClick={onSignOut}
          data-testid="sidebar-nav-logout"
          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-[var(--panini-red)] border-t-2 border-[var(--ink)] hover:bg-[var(--panini-red-soft)] transition"
        >
          <LogOut size={16} /> Logout
        </button>
      </nav>
    </div>
  );
}

function NavItem({ to, state, Icon, label, enabled, onNavigate, testid, end, indent }) {
  const padLeft = indent ? "pl-10" : "px-4";
  if (!enabled) {
    return (
      <NavLink
        to={to}
        end={end ?? (to === "/dashboard")}
        onClick={onNavigate}
        data-testid={testid}
        className={({ isActive }) =>
          `flex items-center gap-3 ${padLeft} py-3 text-sm font-bold border-l-4 transition ${
            isActive
              ? "border-[var(--panini-yellow)] bg-[var(--panini-yellow-soft)] text-[var(--ink)]"
              : "border-transparent text-[var(--smoke)] hover:bg-[var(--cream)] hover:text-[var(--ink)]"
          }`
        }
      >
        <Icon size={16} className="shrink-0" />
        <span className="flex-1 truncate">{label}</span>
        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-[var(--stone)] text-[var(--smoke)] mr-3">
          Soon
        </span>
      </NavLink>
    );
  }
  return (
    <NavLink
      to={to}
      state={state}
      end={end ?? (to === "/dashboard")}
      onClick={onNavigate}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 ${padLeft} py-3 text-sm font-bold border-l-4 transition ${
          isActive
            ? "border-[var(--panini-red)] bg-[var(--panini-yellow-soft)] text-[var(--ink)]"
            : `border-transparent text-[var(--ink)] hover:bg-[var(--cream)] ${indent ? "text-[var(--smoke)]" : ""}`
        }`
      }
    >
      <Icon size={indent ? 14 : 16} className="shrink-0" />
      <span className="flex-1 truncate">{label}</span>
    </NavLink>
  );
}
